import React, { useCallback, useEffect, useState } from "react";
import { Dropdown, Empty, Table } from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  AddIcon,
  EditIcon,
  MoreIconGrey,
  RecycleBin,
} from "../../constants/svg";
import GenericModal from "../../components/Modals/GenericModal";
import DeleteMemberModal from "../../components/Modals/DeleteMemberModal";
import AddCategoryModal from "../../components/Modals/AddCategoryModal";
import {
  DeleteCategory,
  GetCategoriesWithTags,
} from "../../services/categories/categories";
import { all_category_type, status, tableHeight } from "../../utils/constants";
import { tableLoader, toastMessage } from "../../utils/helper";
import { setReloadCategories } from "../../store/slices/globalSlice";

const CategoriesPage = () => {
  const dispatch = useDispatch();
  const { currentSalonId } = useSelector((state) => state.global);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedData, setSelectedData] = useState(null);
  const [disable, setDisable] = useState(false);
  const [showGenericModal, setShowGenericModal] = useState(false);
  const [isUpdateCategory, setIsUpdateCategory] = useState(false);
  const [showModal, setShowModal] = useState({
    deleteModal: null,
  });

  const fetchCategories = useCallback(async () => {
    if (!currentSalonId) return;
    try {
      setLoading(true);
      const response = await GetCategoriesWithTags({
        is_paginated: 0,
        status: status.active,
        type: all_category_type.RFQ,
        salon_id: currentSalonId,
        custom_only: 1,
      });
      const rows = response?.data?.data || [];
      setCategories(Array.isArray(rows) ? rows : []);
    } catch (e) {
      toastMessage(
        "error",
        e?.response?.data?.message || e.message || "Failed to load categories",
      );
    } finally {
      setLoading(false);
    }
  }, [currentSalonId]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const closeCategoryModal = () => {
    setShowGenericModal(false);
    setIsUpdateCategory(false);
    setSelectedData(null);
  };

  const handleDelete = async () => {
    if (!selectedData?.id) return;
    try {
      setDisable(true);
      const response = await DeleteCategory(selectedData.id);
      if (response?.data?.status === false) {
        toastMessage(
          "error",
          response?.data?.message || "Failed to delete category",
        );
        return;
      }
      toastMessage(
        "success",
        response?.data?.message || "Category deleted successfully",
      );
      dispatch(setReloadCategories());
      setShowModal({ deleteModal: false });
      fetchCategories();
    } catch (e) {
      toastMessage(
        "error",
        e?.response?.data?.message || e.message || "Failed to delete category",
      );
    } finally {
      setDisable(false);
    }
  };

  const actionMenu = (record) => [
    {
      key: "edit",
      label: (
        <div
          className="black item"
          onClick={() => {
            setSelectedData(record);
            setIsUpdateCategory(true);
            setShowGenericModal(true);
          }}
        >
          <EditIcon />
          Edit Category
        </div>
      ),
    },
    {
      key: "delete",
      label: (
        <div
          className="deleteLabel"
          onClick={() => {
            setSelectedData(record);
            setShowModal({ deleteModal: true });
          }}
        >
          <RecycleBin />
          Delete
        </div>
      ),
    },
  ];

  const columns = [
    {
      title: "Category Name",
      dataIndex: "name",
      key: "name",
      width: "32%",
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
      render: (name) => (
        <span className="categoriesTable__name">{name || "-"}</span>
      ),
    },
    {
      title: "Category Tags",
      key: "tags",
      width: "58%",
      render: (_, record) => {
        const tags = record.categoryTags || record.category_tags || [];
        if (!tags.length) {
          return <span className="categoriesTable__tagsEmpty">—</span>;
        }
        return (
          <span className="categoriesTable__tags">
            {tags
              .map((tag) => tag.name)
              .filter(Boolean)
              .join(", ")}
          </span>
        );
      },
    },
    {
      title: "More",
      key: "more",
      width: "10%",
      align: "right",
      render: (_, record) => (
        <Dropdown
          menu={{ items: actionMenu(record) }}
          placement="bottomRight"
          trigger={["click"]}
          overlayClassName="defaultDropdown balckedDropdown newdrop"
        >
          <div className="icon cursor-pointer d-inline-flex">
            <MoreIconGrey />
          </div>
        </Dropdown>
      ),
    },
  ];

  return (
    <>
      <div className="teamTabMainHead">
        <h2 className="sec-heading">Categories</h2>
        <button
          className="btn-gradient"
          onClick={() => {
            setIsUpdateCategory(false);
            setSelectedData(null);
            setShowGenericModal(true);
          }}
        >
          <span>
            <AddIcon />
          </span>
          Add Category
        </button>
      </div>

      <div className="teamTabSubHead memberHeader">
        <p>
          Create custom categories for your resources and quizzes.{" "}
          <a
            href="https://support.mysalonsymphony.com/hc/en-us"
            target="_blank"
            className="uLine globalBlueLinkColor"
            rel="noreferrer"
          >
            Learn more.
          </a>
        </p>
      </div>

      <div className="teamBody content">
        <Table
          columns={columns}
          dataSource={categories}
          rowKey={(record) => record.id}
          loading={tableLoader(loading)}
          pagination={false}
          bordered={false}
          size="large"
          showSorterTooltip={false}
          className="categoriesTable"
          scroll={{ y: tableHeight?.teamHeightTable }}
          locale={{
            emptyText: (
              <Empty description="No custom categories yet. Click Add Category to create one." />
            ),
          }}
        />
      </div>

      <DeleteMemberModal
        disable={disable}
        handleDelete={handleDelete}
        show={showModal.deleteModal}
        onHide={() => setShowModal({ ...showModal, deleteModal: false })}
        member={{ name: selectedData?.name || "category" }}
        customMessage="Deleting this category will remove it from your workspace category dropdowns. Categories that are still assigned to resources, quizzes, or flashcards cannot be deleted."
      />

      {showGenericModal && (
        <GenericModal
          show={showGenericModal}
          onHide={closeCategoryModal}
          setModalShow={closeCategoryModal}
          disableLeaveConfirmation={true}
          modalTitle={isUpdateCategory ? "Edit Category" : "Create Category"}
          customModalHeader={"customCategoryModalHeader"}
          customClassName={"create-category-modal"}
          size={"lg"}
          bodyComponent={
            <AddCategoryModal
              setModalShow={closeCategoryModal}
              selectedData={selectedData}
              isUpdateCategory={isUpdateCategory}
              onSaved={fetchCategories}
              customModalBody={"customCategoryModalBody"}
            />
          }
        />
      )}
    </>
  );
};

export default CategoriesPage;
